<template>
    <div class="adsets-list">
        <div class="header">
            <div class="title-block">
                <div class="title">{{ $t("metaInsights.adSets") }}</div>
                <div class="subtitle">
                    <template v-if="campaignId">
                        {{ $t("metaInsights.adSetsForCampaign") }}: <strong>{{ campaignId }}</strong>
                        <a class="clear-filter" @click="$emit('clear-filter')">
                            <i class="material-icons">close</i>{{ $t("metaInsights.clearFilter") }}
                        </a>
                    </template>
                    <template v-else>{{ $t("metaInsights.adSetsDesc") }}</template>
                </div>
            </div>
            <div class="toolbar">
                <DateRangePicker v-model="dateRange" @change="loadAdSets" />
                <SupsisButton ghost @click="loadAdSets" :disabled="loading">
                    <i class="material-icons" :class="{ spinning: loading }">refresh</i>
                    {{ $t("metaInsights.refresh") }}
                </SupsisButton>
            </div>
        </div>

        <a-spin :spinning="loading">
            <InsightsTable
                :rows="adsets"
                :currency="currency"
                table-id="meta-insights-adsets-table"
                :name-label-key="'metaInsights.col.adSet'"
                :clickable="true"
                @row-click="onRowClick"
            />
        </a-spin>

        <div v-if="hasMore" class="load-more-wrap">
            <SupsisButton ghost :disabled="loadingMore" @click="loadMore">
                <i v-if="loadingMore" class="material-icons spinning">refresh</i>
                <i v-else class="material-icons">expand_more</i>
                {{ loadingMore ? $t("metaInsights.loading") : $t("metaInsights.loadMore") }}
            </SupsisButton>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import DateRangePicker from "./DateRangePicker.vue";
import InsightsTable from "./InsightsTable.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import { INSIGHTS_API } from "../../../../../api/meta";

export default {
    name: "AdSetsList",
    components: {
        DateRangePicker,
        InsightsTable,
        SupsisButton,
    },
    props: {
        channel: {
            type: Object,
            required: true,
        },
        campaignId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            dateRange: { datePreset: "last_30d" },
            adsets: [],
            loading: false,
            loadingMore: false,
            afterCursor: null,
            hasMore: false,
            pageSize: 25,
        };
    },
    computed: {
        currency() {
            return _.get(this.channel, "payload.selectedAdAccount.currency", "");
        },
    },
    methods: {
        async loadAdSets(append) {
            if (!this.channel) return;
            const isAppend = !!append;
            if (isAppend) {
                this.loadingMore = true;
            } else {
                this.loading = true;
                this.afterCursor = null;
            }
            try {
                const params = Object.assign({}, this.dateRange, {
                    limit: this.pageSize,
                    after: isAppend ? this.afterCursor : null,
                });
                const res = await INSIGHTS_API.listAdSets(this.channel._id, this.campaignId, params);
                const items = _.get(res, "data.data") || [];
                const paging = _.get(res, "data.paging") || {};
                this.adsets = isAppend ? this.adsets.concat(items) : items;
                this.afterCursor = _.get(paging, "cursors.after") || null;
                this.hasMore = !!paging.next;
            } catch (err) {
                console.error(err);
                this.$message.error(this.$t("metaInsights.failedToLoadAdSets"));
                if (!isAppend) this.adsets = [];
            } finally {
                this.loading = false;
                this.loadingMore = false;
            }
        },
        loadMore() {
            this.loadAdSets(true);
        },
        onRowClick(row) {
            this.$emit("drill-down", { type: "adset", row });
        },
    },
    mounted() {
        this.loadAdSets();
    },
    watch: {
        campaignId() {
            this.loadAdSets();
        },
    },
};
</script>

<style scoped lang="scss">
.adsets-list {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 12px;
        padding-bottom: 12px;
        border-bottom: 1px solid var(--color-border-default);

        .title-block {
            display: flex;
            flex-direction: column;
            gap: 2px;

            .title {
                font-size: 18px;
                font-weight: 700;
                color: var(--color-text-strong-default);
            }

            .subtitle {
                font-size: 13px;
                color: var(--color-text-muted-default);

                .clear-filter {
                    margin-left: 10px;
                    color: var(--color-text-info-default);
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;

                    i {
                        font-size: 14px;
                    }
                }
            }
        }

        .toolbar {
            display: flex;
            align-items: center;
            gap: 8px;
        }
    }

    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }

    .spinning {
        animation: spin 1s linear infinite;
    }

    .load-more-wrap {
        display: flex;
        justify-content: center;
        padding: 12px 0 4px;
    }
}
</style>
